import React, { useState, useEffect } from 'react';
import { 
  FileText, 
  Plus, 
  Search, 
  Eye,
  AlertTriangle,
  CheckCircle,
  Shield,
  TrendingUp,
  X
} from 'lucide-react';
import axios from 'axios';

const API_URL = (window.__BACKEND_URL__ || process.env.REACT_APP_BACKEND_URL) || 'http://localhost:8001';

// Modal de création de main courante
const CreateMainCouranteModal = ({ onClose, onSuccess }) => {
  const [sites, setSites] = useState([]);
  const [agents, setAgents] = useState([]);
  const [formData, setFormData] = useState({
    type_incident: 'vol_etalage',
    date_incident: new Date().toISOString().split('T')[0],
    heure_incident: new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }),
    site_id: '',
    lieu_precis: '',
    agent_constatant_id: '',
    description: '',
    circonstances: '',
    
    // Mis en cause
    mis_en_cause: {
      nom: '',
      prenom: '',
      date_naissance: '',
      adresse: '',
      nationalite: '',
      piece_identite: '',
      numero_piece: ''
    },
    mis_en_cause_mineur: false,
    parents_contactes: false,
    
    // Articles volés
    articles_voles: [],
    montant_total_vol: 0,
    articles_recuperes: false,
    restitution_articles: false,
    
    // Démarque
    type_demarque: '',
    rayons_concernes: [],
    montant_demarque: 0,
    
    // Interpellation
    interpellation_effectuee: false,
    heure_interpellation: '',
    lieu_interpellation: '',
    motif_interpellation: '',
    comportement_interpelle: 'cooperatif',
    
    // Séquestre
    sequestre_effectue: false,
    heure_debut_sequestre: '',
    heure_fin_sequestre: '',
    motif_sequestre: '',
    droits_informes: false,
    
    // Autorités
    police_appelee: false,
    heure_appel_police: '',
    commissariat: '',
    numero_main_courante_police: '',
    depot_plainte: false,
    numero_plainte: '',
    
    // Documents
    photos_prises: false,
    video_surveillance: false,
    numero_bande_video: '',
    piece_identite_copiee: false,
    
    // Autres
    observations: '',
    statut: 'brouillon'
  });
  
  const [currentArticle, setCurrentArticle] = useState({
    designation: '',
    quantite: 1,
    prix_unitaire: 0
  });
  
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadSites();
    loadAgents();
  }, []);

  const loadSites = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/sites`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSites(response.data);
    } catch (error) {
      console.error('Erreur chargement sites:', error);
    }
  };

  const loadAgents = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/agents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAgents(response.data);
    } catch (error) {
      console.error('Erreur chargement agents:', error);
    }
  };

  const handleAddArticle = () => {
    if (currentArticle.designation && currentArticle.prix_unitaire > 0) {
      const montant = currentArticle.quantite * currentArticle.prix_unitaire;
      const newArticle = { ...currentArticle, montant_total: montant };
      
      const newArticles = [...formData.articles_voles, newArticle];
      const total = newArticles.reduce((sum, art) => sum + art.montant_total, 0);
      
      setFormData({
        ...formData,
        articles_voles: newArticles,
        montant_total_vol: total
      });
      
      setCurrentArticle({ designation: '', quantite: 1, prix_unitaire: 0 });
    }
  };

  const handleRemoveArticle = (index) => {
    const newArticles = formData.articles_voles.filter((_, i) => i !== index);
    const total = newArticles.reduce((sum, art) => sum + art.montant_total, 0);
    
    setFormData({
      ...formData,
      articles_voles: newArticles,
      montant_total_vol: total
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const token = localStorage.getItem('token');
      
      // Trouver le nom du site et de l'agent
      const site = sites.find(s => s.id === formData.site_id);
      const agent = agents.find(a => a.id === formData.agent_constatant_id);
      
      const submitData = {
        ...formData,
        site_name: site?.name || '',
        agent_constatant_name: agent?.name || ''
      };

      await axios.post(`${API_URL}/api/main-courante/`, submitData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Erreur création main courante:', error);
      alert('Erreur lors de la création de la main courante');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg p-6 max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-indigo-600">Créer une main courante</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Type d'incident */}
          <div className="mb-6 bg-gray-50 p-4 rounded-lg">
            <label className="block text-sm font-semibold mb-2">Type d'incident *</label>
            <select
              value={formData.type_incident}
              onChange={(e) => setFormData({ ...formData, type_incident: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
              required
            >
              <option value="vol_etalage">🛒 Vol à l'étalage</option>
              <option value="demarque_inconnue">📉 Démarque inconnue</option>
              <option value="interpellation">🚔 Interpellation</option>
              <option value="intrusion">🚪 Intrusion</option>
              <option value="agression">⚠️ Agression</option>
              <option value="accident">🚑 Accident</option>
              <option value="autre">📝 Autre</option>
            </select>
          </div>

          {/* Date, heure, lieu */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-3">📍 Contexte</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm mb-1">Date incident *</label>
                <input
                  type="date"
                  value={formData.date_incident}
                  onChange={(e) => setFormData({ ...formData, date_incident: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  required
                />
              </div>
              <div>
                <label className="block text-sm mb-1">Heure incident *</label>
                <input
                  type="time"
                  value={formData.heure_incident}
                  onChange={(e) => setFormData({ ...formData, heure_incident: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  required
                />
              </div>
              <div>
                <label className="block text-sm mb-1">Site *</label>
                <select
                  value={formData.site_id}
                  onChange={(e) => setFormData({ ...formData, site_id: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  required
                >
                  <option value="">-- Site --</option>
                  {sites.map(site => (
                    <option key={site.id} value={site.id}>{site.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm mb-1">Agent constatant *</label>
                <select
                  value={formData.agent_constatant_id}
                  onChange={(e) => setFormData({ ...formData, agent_constatant_id: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  required
                >
                  <option value="">-- Agent --</option>
                  {agents.map(agent => (
                    <option key={agent.id} value={agent.id}>{agent.name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="mt-4">
              <label className="block text-sm mb-1">Lieu précis *</label>
              <input
                type="text"
                placeholder="Ex: Rayon informatique, Parking, Entrée principale..."
                value={formData.lieu_precis}
                onChange={(e) => setFormData({ ...formData, lieu_precis: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
                required
              />
            </div>
          </div>

          {/* Description */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-3">📝 Description</h3>
            <textarea
              placeholder="Description détaillée de l'incident..."
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg"
              rows="3"
              required
            />
            <textarea
              placeholder="Circonstances (optionnel)..."
              value={formData.circonstances}
              onChange={(e) => setFormData({ ...formData, circonstances: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg mt-2"
              rows="2"
            />
          </div>

          {/* VOL À L'ÉTALAGE */}
          {formData.type_incident === 'vol_etalage' && (
            <div className="mb-6 bg-red-50 p-4 rounded-lg border border-red-200">
              <h3 className="text-lg font-semibold mb-3 text-red-900">🛒 Articles volés</h3>
              
              {/* Liste des articles */}
              {formData.articles_voles.length > 0 && (
                <div className="mb-4">
                  <table className="w-full text-sm">
                    <thead className="bg-red-100">
                      <tr>
                        <th className="text-left p-2">Article</th>
                        <th className="text-left p-2">Qté</th>
                        <th className="text-left p-2">Prix unit.</th>
                        <th className="text-left p-2">Total</th>
                        <th className="p-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {formData.articles_voles.map((art, idx) => (
                        <tr key={`${art.designation}-${art.prix_unitaire}-${idx}`} className="border-b">
                          <td className="p-2">{art.designation}</td>
                          <td className="p-2">{art.quantite}</td>
                          <td className="p-2">{art.prix_unitaire.toFixed(2)}€</td>
                          <td className="p-2 font-bold">{art.montant_total.toFixed(2)}€</td>
                          <td className="p-2">
                            <button
                              type="button"
                              onClick={() => handleRemoveArticle(idx)}
                              className="text-red-600 hover:text-red-800"
                            >
                              <X size={16} />
                            </button>
                          </td>
                        </tr>
                      ))}
                      <tr className="bg-red-100 font-bold">
                        <td colSpan="3" className="p-2 text-right">TOTAL:</td>
                        <td className="p-2">{formData.montant_total_vol.toFixed(2)}€</td>
                        <td></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
              
              {/* Ajouter article */}
              <div className="grid grid-cols-4 gap-2">
                <input
                  type="text"
                  placeholder="Désignation"
                  value={currentArticle.designation}
                  onChange={(e) => setCurrentArticle({ ...currentArticle, designation: e.target.value })}
                  className="px-3 py-2 border rounded-lg"
                />
                <input
                  type="number"
                  placeholder="Qté"
                  value={currentArticle.quantite}
                  onChange={(e) => setCurrentArticle({ ...currentArticle, quantite: parseInt(e.target.value) })}
                  className="px-3 py-2 border rounded-lg"
                  min="1"
                />
                <input
                  type="number"
                  placeholder="Prix unitaire"
                  value={currentArticle.prix_unitaire}
                  onChange={(e) => setCurrentArticle({ ...currentArticle, prix_unitaire: parseFloat(e.target.value) })}
                  className="px-3 py-2 border rounded-lg"
                  step="0.01"
                />
                <button
                  type="button"
                  onClick={handleAddArticle}
                  className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700"
                >
                  + Ajouter
                </button>
              </div>
              
              <div className="mt-4 grid grid-cols-2 gap-4">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={formData.articles_recuperes}
                    onChange={(e) => setFormData({ ...formData, articles_recuperes: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">Articles récupérés</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={formData.restitution_articles}
                    onChange={(e) => setFormData({ ...formData, restitution_articles: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">Restitution effectuée</span>
                </label>
              </div>
            </div>
          )}

          {/* DÉMARQUE INCONNUE */}
          {formData.type_incident === 'demarque_inconnue' && (
            <div className="mb-6 bg-orange-50 p-4 rounded-lg border border-orange-200">
              <h3 className="text-lg font-semibold mb-3 text-orange-900">📉 Démarque inconnue</h3>
              <div className="grid grid-cols-2 gap-4">
                <select
                  value={formData.type_demarque}
                  onChange={(e) => setFormData({ ...formData, type_demarque: e.target.value })}
                  className="px-3 py-2 border rounded-lg"
                >
                  <option value="">-- Type démarque --</option>
                  <option value="casse">Casse</option>
                  <option value="peremption">Péremption</option>
                  <option value="vol_interne">Vol interne</option>
                  <option value="erreur_inventaire">Erreur inventaire</option>
                </select>
                <input
                  type="number"
                  placeholder="Montant démarque (€)"
                  value={formData.montant_demarque}
                  onChange={(e) => setFormData({ ...formData, montant_demarque: parseFloat(e.target.value) })}
                  className="px-3 py-2 border rounded-lg"
                  step="0.01"
                />
              </div>
            </div>
          )}

          {/* MIS EN CAUSE */}
          <div className="mb-6 bg-yellow-50 p-4 rounded-lg border border-yellow-200">
            <h3 className="text-lg font-semibold mb-3 text-yellow-900">👤 Personne mise en cause</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <input
                type="text"
                placeholder="Nom"
                value={formData.mis_en_cause.nom}
                onChange={(e) => setFormData({
                  ...formData,
                  mis_en_cause: { ...formData.mis_en_cause, nom: e.target.value }
                })}
                className="px-3 py-2 border rounded-lg"
              />
              <input
                type="text"
                placeholder="Prénom"
                value={formData.mis_en_cause.prenom}
                onChange={(e) => setFormData({
                  ...formData,
                  mis_en_cause: { ...formData.mis_en_cause, prenom: e.target.value }
                })}
                className="px-3 py-2 border rounded-lg"
              />
              <input
                type="date"
                placeholder="Date naissance"
                value={formData.mis_en_cause.date_naissance}
                onChange={(e) => setFormData({
                  ...formData,
                  mis_en_cause: { ...formData.mis_en_cause, date_naissance: e.target.value }
                })}
                className="px-3 py-2 border rounded-lg"
              />
              <input
                type="text"
                placeholder="Adresse"
                value={formData.mis_en_cause.adresse}
                onChange={(e) => setFormData({
                  ...formData,
                  mis_en_cause: { ...formData.mis_en_cause, adresse: e.target.value }
                })}
                className="px-3 py-2 border rounded-lg col-span-2"
              />
              <input
                type="text"
                placeholder="Pièce d'identité"
                value={formData.mis_en_cause.piece_identite}
                onChange={(e) => setFormData({
                  ...formData,
                  mis_en_cause: { ...formData.mis_en_cause, piece_identite: e.target.value }
                })}
                className="px-3 py-2 border rounded-lg"
              />
            </div>
            <div className="mt-4 flex gap-4">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={formData.mis_en_cause_mineur}
                  onChange={(e) => setFormData({ ...formData, mis_en_cause_mineur: e.target.checked })}
                  className="w-4 h-4"
                />
                <span className="text-sm">Mineur</span>
              </label>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={formData.parents_contactes}
                  onChange={(e) => setFormData({ ...formData, parents_contactes: e.target.checked })}
                  className="w-4 h-4"
                />
                <span className="text-sm">Parents contactés</span>
              </label>
            </div>
          </div>

          {/* INTERPELLATION */}
          <div className="mb-6 bg-purple-50 p-4 rounded-lg border border-purple-200">
            <h3 className="text-lg font-semibold mb-3 text-purple-900">🚔 Interpellation</h3>
            <label className="flex items-center space-x-2 mb-4">
              <input
                type="checkbox"
                checked={formData.interpellation_effectuee}
                onChange={(e) => setFormData({ ...formData, interpellation_effectuee: e.target.checked })}
                className="w-4 h-4"
              />
              <span className="font-medium">Interpellation effectuée</span>
            </label>
            
            {formData.interpellation_effectuee && (
              <div className="grid grid-cols-2 gap-4">
                <input
                  type="time"
                  placeholder="Heure interpellation"
                  value={formData.heure_interpellation}
                  onChange={(e) => setFormData({ ...formData, heure_interpellation: e.target.value })}
                  className="px-3 py-2 border rounded-lg"
                />
                <select
                  value={formData.comportement_interpelle}
                  onChange={(e) => setFormData({ ...formData, comportement_interpelle: e.target.value })}
                  className="px-3 py-2 border rounded-lg"
                >
                  <option value="cooperatif">Coopératif</option>
                  <option value="agressif">Agressif</option>
                  <option value="fuite">Tentative de fuite</option>
                </select>
                <input
                  type="text"
                  placeholder="Lieu interpellation"
                  value={formData.lieu_interpellation}
                  onChange={(e) => setFormData({ ...formData, lieu_interpellation: e.target.value })}
                  className="px-3 py-2 border rounded-lg col-span-2"
                />
                <textarea
                  placeholder="Motif interpellation"
                  value={formData.motif_interpellation}
                  onChange={(e) => setFormData({ ...formData, motif_interpellation: e.target.value })}
                  className="px-3 py-2 border rounded-lg col-span-2"
                  rows="2"
                />
              </div>
            )}
          </div>

          {/* AUTORITÉS */}
          <div className="mb-6 bg-blue-50 p-4 rounded-lg border border-blue-200">
            <h3 className="text-lg font-semibold mb-3 text-blue-900">🚓 Autorités</h3>
            <div className="space-y-3">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={formData.police_appelee}
                  onChange={(e) => setFormData({ ...formData, police_appelee: e.target.checked })}
                  className="w-4 h-4"
                />
                <span>Police appelée</span>
              </label>
              
              {formData.police_appelee && (
                <div className="grid grid-cols-3 gap-4 ml-6">
                  <input
                    type="time"
                    placeholder="Heure appel"
                    value={formData.heure_appel_police}
                    onChange={(e) => setFormData({ ...formData, heure_appel_police: e.target.value })}
                    className="px-3 py-2 border rounded-lg"
                  />
                  <input
                    type="text"
                    placeholder="Commissariat"
                    value={formData.commissariat}
                    onChange={(e) => setFormData({ ...formData, commissariat: e.target.value })}
                    className="px-3 py-2 border rounded-lg"
                  />
                  <input
                    type="text"
                    placeholder="N° MC Police"
                    value={formData.numero_main_courante_police}
                    onChange={(e) => setFormData({ ...formData, numero_main_courante_police: e.target.value })}
                    className="px-3 py-2 border rounded-lg"
                  />
                </div>
              )}
              
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={formData.depot_plainte}
                  onChange={(e) => setFormData({ ...formData, depot_plainte: e.target.checked })}
                  className="w-4 h-4"
                />
                <span>Dépôt de plainte</span>
              </label>
            </div>
          </div>

          {/* Observations */}
          <div className="mb-6">
            <label className="block text-sm font-semibold mb-2">Observations</label>
            <textarea
              value={formData.observations}
              onChange={(e) => setFormData({ ...formData, observations: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg"
              rows="3"
              placeholder="Observations complémentaires..."
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-500 text-white px-6 py-3 rounded-lg hover:bg-gray-600"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 bg-indigo-600 text-white px-6 py-3 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              {saving ? 'Création...' : 'Créer la main courante'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const MainCourante = () => {
  const [incidents, setIncidents] = useState([]);
  const [stats, setStats] = useState({
    total_incidents: 0,
    par_type: {},
    montant_total_vols: 0,
    montant_total_demarque: 0,
    taux_interpellation: 0,
    incidents_police: 0
  });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatut, setFilterStatut] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState(null);

  useEffect(() => {
    loadIncidents();
    loadStats();
  }, [filterType, filterStatut]);

  const loadIncidents = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = {};
      if (filterType !== 'all') params.type_incident = filterType;
      if (filterStatut !== 'all') params.statut = filterStatut;

      const response = await axios.get(`${API_URL}/api/main-courante/`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setIncidents(response.data);
    } catch (error) {
      console.error('Erreur chargement incidents:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/main-courante/stats/general`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Erreur chargement stats:', error);
    }
  };

  const getTypeLabel = (type) => {
    const types = {
      'vol_etalage': '🛒 Vol à l\'étalage',
      'demarque_inconnue': '📉 Démarque inconnue',
      'interpellation': '🚔 Interpellation',
      'intrusion': '🚪 Intrusion',
      'agression': '⚠️ Agression',
      'accident': '🚑 Accident',
      'autre': '📝 Autre'
    };
    return types[type] || type;
  };

  const getStatutBadge = (statut) => {
    switch (statut) {
      case 'brouillon':
        return <span className="bg-gray-100 text-gray-800 text-xs px-2 py-1 rounded-full">Brouillon</span>;
      case 'validee':
        return <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full flex items-center gap-1"><CheckCircle size={12} /> Validée</span>;
      case 'transmise':
        return <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">Transmise</span>;
      case 'classee':
        return <span className="bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded-full">Classée</span>;
      default:
        return <span className="bg-gray-100 text-gray-800 text-xs px-2 py-1 rounded-full">{statut}</span>;
    }
  };

  const filteredIncidents = incidents.filter(incident =>
    incident.numero?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    incident.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    incident.lieu_precis?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <FileText className="h-8 w-8 text-indigo-600" />
            Main Courante
          </h1>
          <p className="text-gray-600 mt-1">Gestion des incidents de sécurité et rapports</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-indigo-600 text-white px-6 py-3 rounded-lg flex items-center gap-2 hover:bg-indigo-700 transition-colors"
        >
          <Plus className="h-5 w-5" />
          Nouvelle Main Courante
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow-md border-l-4 border-l-blue-500 p-6">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-medium text-gray-600">Total Incidents</div>
            <FileText className="h-5 w-5 text-blue-500" />
          </div>
          <div className="text-3xl font-bold text-blue-600">{stats.total_incidents}</div>
          <div className="text-xs text-gray-500 mt-1">Tous types confondus</div>
        </div>

        <div className="bg-white rounded-lg shadow-md border-l-4 border-l-red-500 p-6">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-medium text-gray-600">Vols Étalage</div>
            <AlertTriangle className="h-5 w-5 text-red-500" />
          </div>
          <div className="text-3xl font-bold text-red-600">
            {stats.montant_total_vols?.toFixed(2) || '0.00'}€
          </div>
          <div className="text-xs text-gray-500 mt-1">Montant total</div>
        </div>

        <div className="bg-white rounded-lg shadow-md border-l-4 border-l-orange-500 p-6">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-medium text-gray-600">Démarque</div>
            <TrendingUp className="h-5 w-5 text-orange-500" />
          </div>
          <div className="text-3xl font-bold text-orange-600">
            {stats.montant_total_demarque?.toFixed(2) || '0.00'}€
          </div>
          <div className="text-xs text-gray-500 mt-1">Pertes & casse</div>
        </div>

        <div className="bg-white rounded-lg shadow-md border-l-4 border-l-purple-500 p-6">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-medium text-gray-600">Interpellations</div>
            <Shield className="h-5 w-5 text-purple-500" />
          </div>
          <div className="text-3xl font-bold text-purple-600">{stats.taux_interpellation}%</div>
          <div className="text-xs text-gray-500 mt-1">Taux d'interpellation</div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-md p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <input
              type="text"
              placeholder="Rechercher (N°, description, lieu)..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
          >
            <option value="all">Tous types</option>
            <option value="vol_etalage">Vol à l'étalage</option>
            <option value="demarque_inconnue">Démarque inconnue</option>
            <option value="interpellation">Interpellation</option>
            <option value="intrusion">Intrusion</option>
            <option value="agression">Agression</option>
            <option value="accident">Accident</option>
          </select>

          <select
            value={filterStatut}
            onChange={(e) => setFilterStatut(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
          >
            <option value="all">Tous statuts</option>
            <option value="brouillon">Brouillon</option>
            <option value="validee">Validée</option>
            <option value="transmise">Transmise</option>
            <option value="classee">Classée</option>
          </select>
        </div>
      </div>

      {/* Liste des incidents */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Chargement...</div>
        ) : filteredIncidents.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <FileText className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p>Aucun incident trouvé</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="mt-4 bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700"
            >
              Créer la première main courante
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">N°</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Type</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Date/Heure</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Lieu</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Agent</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Montant</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Statut</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredIncidents.map((incident) => (
                  <tr key={incident.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-mono font-medium">{incident.numero}</td>
                    <td className="px-4 py-3 text-sm">{getTypeLabel(incident.type_incident)}</td>
                    <td className="px-4 py-3 text-sm">
                      {new Date(incident.date_incident).toLocaleDateString('fr-FR')}
                      <br/>
                      <span className="text-xs text-gray-500">{incident.heure_incident}</span>
                    </td>
                    <td className="px-4 py-3 text-sm">{incident.lieu_precis}</td>
                    <td className="px-4 py-3 text-sm">{incident.agent_constatant_name}</td>
                    <td className="px-4 py-3 text-sm">
                      {incident.montant_total_vol > 0 && (
                        <span className="text-red-600 font-medium">
                          {incident.montant_total_vol.toFixed(2)}€
                        </span>
                      )}
                      {incident.montant_demarque > 0 && (
                        <span className="text-orange-600 font-medium">
                          {incident.montant_demarque.toFixed(2)}€
                        </span>
                      )}
                      {!incident.montant_total_vol && !incident.montant_demarque && '-'}
                    </td>
                    <td className="px-4 py-3">{getStatutBadge(incident.statut)}</td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => setSelectedIncident(incident)}
                        className="p-1 hover:bg-indigo-50 rounded"
                        title="Voir détails"
                      >
                        <Eye size={16} className="text-indigo-600" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal Create */}
      {showCreateModal && <CreateMainCouranteModal onClose={() => setShowCreateModal(false)} onSuccess={loadIncidents} />}
    </div>
  );
};

export default MainCourante;
